import { useState } from 'react';
import { motion } from 'framer-motion';
import { Globe, CheckCircle, Copy, ExternalLink, Loader2, AlertCircle, Code } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${window.location.origin}/api`;

export default function DomainVerification({ businessId, website, isVerified, onVerified }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Generate Token, 2: Add Tag, 3: Verify
  const [token, setToken] = useState(null);
  const [metaTag, setMetaTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const generateToken = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/generate-verification-token`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate token');
      }

      setToken(data.token);
      setMetaTag(data.meta_tag);
      setStep(2);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyDomain = async () => {
    setVerifying(true);
    try {
      const response = await fetch(`${API}/businesses/${businessId}/verify-domain`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      if (data.verified) {
        toast.success(data.message);
        setStep(3);
        onVerified?.();
        setTimeout(() => setDialogOpen(false), 2000);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  if (isVerified) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
        <CheckCircle className="w-4 h-4 text-green-400" />
        <span className="text-green-400 text-sm font-medium">Domain Verified</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => {
          setDialogOpen(true);
          setStep(1);
        }}
        disabled={!website}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-400 hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="claim-site-btn"
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm font-medium">Claim This Site</span>
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-400" />
              Verify Domain Ownership
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Prove you own {website?.replace('https://', '').replace('http://', '').split('/')[0]} to get the verified badge
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Progress Steps */}
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-zinc-800 text-zinc-500'
                  }`}>
                    {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-16 h-0.5 mx-2 transition-colors ${
                      step > s ? 'bg-blue-500' : 'bg-zinc-800'
                    }`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Generate Token */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <h4 className="text-white font-medium mb-2">Step 1: Generate Verification Token</h4>
                  <p className="text-zinc-400 text-sm">
                    We&apos;ll create a unique code that you&apos;ll add to your website to prove ownership.
                  </p>
                </div>

                <button
                  onClick={generateToken}
                  disabled={loading}
                  className="w-full py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="generate-token-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Code className="w-4 h-4" />
                      Generate Token
                    </>
                  )}
                </button>
              </motion.div>
            )}

            {/* Step 2: Add Meta Tag */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
                  <h4 className="text-white font-medium mb-2">Step 2: Add Meta Tag to Your Website</h4>
                  <p className="text-zinc-400 text-sm mb-4">
                    Copy this code and paste it into your website&apos;s &lt;head&gt; section:
                  </p>

                  <div className="relative">
                    <pre className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 text-green-400 text-sm overflow-x-auto">
                      {metaTag}
                    </pre>
                    <button
                      onClick={() => copyToClipboard(metaTag)}
                      className="absolute top-2 right-2 p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors"
                      data-testid="copy-meta-tag"
                    >
                      <Copy className="w-4 h-4 text-zinc-400" />
                    </button>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5" />
                    <p className="text-yellow-400/80 text-xs">
                      The meta tag must be in the &lt;head&gt; section of your homepage. 
                      If you use a website builder (Shopify, Squarespace, etc.), look for 
                      &quot;Custom Code&quot; or &quot;Header Scripts&quot; settings.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <a
                    href={website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 py-2 rounded-lg bg-zinc-800 text-white text-center hover:bg-zinc-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open Website
                  </a>
                  <button
                    onClick={verifyDomain}
                    disabled={verifying}
                    className="flex-1 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="verify-domain-btn"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Verify Domain
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Success */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-6"
              >
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Domain Verified!</h3>
                <p className="text-zinc-400">
                  Your website ownership has been verified. You&apos;ve earned 2 MEL!
                </p>
              </motion.div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
